import {naturalSortByKey} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {redirectForDownload, setWindowTitle} from 'BenchlingUtil';
import {FONT_PROXIMA_10} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import {Header1} from 'ui-library/Headers';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import TextField from 'ui-library/inputs/TextField';
import legacyRunModal from 'ui-library/legacyRunModal';
import Modal from 'ui-library/modals/Modal';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {confirm} from 'util/Modals';
import {capitalize} from 'util/NameUtil';
import {getUserMessage} from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import CreateInstrumentGatewayModal from './CreateInstrumentGatewayModal';
import {GatewayFieldsForm, useGatewayFields} from './GatewayFieldsForm';
import {
  GATEWAY_TYPE_TO_DISPLAY,
  GatewayStatus,
  GatewayType,
  InstrumentGateway,
  LATEST_BASE_GATEWAY_INSTALLER_VERSION,
  S3_FIELD_IDS,
} from './InstrumentGatewayConstants';
import {
  archiveGateway,
  createGateway,
  listGatewaysPage,
  createInstallerTokenFile,
  getGatewayInstaller,
  getS3GatewayInstaller,
  getCompatibilityScript,
  updateGateway,
} from './InstrumentGatewayDAO';
import {CreateS3GatewayModal, ViewBucketInfoModal} from './S3GatewayModal';

interface GatewayCellProps {
  gateway: InstrumentGateway;
}

function archiveGatewayWithMessage(gatewayId: string, refetch: () => void): void {
  const msg =
    'Archiving this gateway will archive all connections associated with it and is not reversible. To re-connect to the instrument, you will need to install a new gateway and create new connections.';
  confirm('Archive Gateway?', msg, ButtonStyle.WARNING, {
    acceptText: 'Archive Gateway',
    rejectText: 'Cancel',
    promiseCompatible: true,
    onSubmit: () => {
      withLoadingScreen(archiveGateway(gatewayId), 'Archiving gateway...').then(() => {
        refetch();
      });
    },
  });
}

interface DownloadGatewaytModalProps {
  onRequestClose: () => void;
}

function DownloadGatewayModal(props: DownloadGatewaytModalProps): JSX.Element {
  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.TERTIARY_GRAY,
          text: 'Generate installer token',
          onClick: async () => {
            const {downloadUrl} = await createInstallerTokenFile();
            redirectForDownload(downloadUrl);
          },
        },
        {
          style: ButtonStyle.PRIMARY,
          cancel: true,
          text: 'Done',
        },
      ]}
      title="Download gateway"
      onRequestClose={props.onRequestClose}
      closeable={true}
    >
      <div className={css(styles.infoBanner)}>
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text={`The latest version (${LATEST_BASE_GATEWAY_INSTALLER_VERSION}) will be downloaded.`}
        />
      </div>
      The gateway installer can be found in your browser downloads folder. Please additionally download an
      installer token by clicking on the "Generate installer token" button below.
      <br />
      <br />
      During the gateway installation process, you will be prompted to upload this installer token which
      expires in 1 hour.
      <br />
      <br />
      If a new token file needs to be generated once this modal has been closed, you can generate a new token
      from the "Download" dropdown.
    </Modal>
  );
}

function DownloadTokenModal(props: DownloadGatewaytModalProps): JSX.Element {
  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.PRIMARY,
          cancel: true,
          text: 'Done',
        },
      ]}
      title="Generate new installer token"
      onRequestClose={props.onRequestClose}
      closeable={true}
    >
      The installer token file can be found in your browser downloads folder.
      <br />
      <br />
      During the gateway installation process, you will be prompted to upload an installer token which expires
      in 1 hour.
    </Modal>
  );
}

function DownloadScriptModal(props: DownloadGatewaytModalProps): JSX.Element {
  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.TERTIARY_GRAY,
          text: 'Generate installer token',
          onClick: async () => {
            const {downloadUrl} = await createInstallerTokenFile();
            redirectForDownload(downloadUrl);
          },
        },
        {
          style: ButtonStyle.PRIMARY,
          cancel: true,
          text: 'Done',
        },
      ]}
      title="Download environment check script"
      onRequestClose={props.onRequestClose}
      closeable={true}
    >
      The PC environment compatibility check script can be found in your browser downloads folder. Please
      additionally download an installer token by clicking on the "Generate installer token" button below.
      <br />
      <br />
      The environment compatibility check script will prompt you to select this installer token, which expires
      in 1 hour.
      <br />
      <br />
      If a new token file needs to be generated once this modal has been closed, you can generate a new token
      from the "Download" dropdown.
    </Modal>
  );
}

const GATEWAY_STATUS_TO_STYLE: {[status in GatewayStatus]: keyof typeof styles} = {
  [GatewayStatus.HEALTHY]: 'healthy',
  [GatewayStatus.UNHEALTHY]: 'unhealthy',
  [GatewayStatus.UNREGISTERED]: 'unregistered',
  [GatewayStatus.GREENGRASS_NUCLEUS_STALE]: 'unhealthy',
  [GatewayStatus.GREENGRASS_COMMUNICATION_ERROR]: 'warning',
  [GatewayStatus.GREENGRASS_DEPLOYING]: 'unregistered',
  [GatewayStatus.S3_BUCKET_ERROR]: 'unhealthy',
};

const GATEWAY_STATUS_TO_TEXT: {[status in GatewayStatus]: string} = {
  [GatewayStatus.HEALTHY]: 'Open',
  [GatewayStatus.UNHEALTHY]: 'Error',
  [GatewayStatus.UNREGISTERED]: 'Pending install',
  [GatewayStatus.GREENGRASS_NUCLEUS_STALE]: 'Error',
  [GatewayStatus.GREENGRASS_COMMUNICATION_ERROR]: 'Unreachable',
  [GatewayStatus.GREENGRASS_DEPLOYING]: 'Deploying',
  [GatewayStatus.S3_BUCKET_ERROR]: 'Error',
};

const GATEWAY_STATUS_TO_TOOLTIP: {[status in GatewayStatus]: string} = {
  [GatewayStatus.HEALTHY]: '',
  [GatewayStatus.UNHEALTHY]:
    'The gateway installed on your computer is experiencing technical issues.\n\nPlease contact Benchling Support.',
  [GatewayStatus.UNREGISTERED]: 'Pending installion.',
  [GatewayStatus.GREENGRASS_NUCLEUS_STALE]:
    'Your computer is not communicating with AWS IoT service, which is necessary to run Benchling Connect. Verify that your computer is active and connected to the internet.\n\nIf the issue persists, please contact Benchling Support.',
  [GatewayStatus.GREENGRASS_COMMUNICATION_ERROR]:
    'Benchling is not receiving messages from the computer where the gateway is installed. Verify that your computer is active and connected to the internet.\n\nIf the issue persists, please contact Benchling Support.',
  [GatewayStatus.GREENGRASS_DEPLOYING]:
    'Benchling Gateway updates are in progress. Associated connection(s) may experience intermittent connectivity during this time. Deployment typically takes up to 15 minutes.',
  [GatewayStatus.S3_BUCKET_ERROR]:
    'Benchling cannot access the S3 bucket as configured. Please verify your S3 policy and the configuration.\n\nIf the issue persists, please contact Benchling Support.',
};

const GATEWAY_STATUS_TO_ICON: {[status in GatewayStatus]: BenchlingIconName} = {
  [GatewayStatus.HEALTHY]: 'status-success',
  [GatewayStatus.UNHEALTHY]: 'status-failed',
  [GatewayStatus.UNREGISTERED]: 'request-in-progress',
  [GatewayStatus.GREENGRASS_NUCLEUS_STALE]: 'status-failed',
  [GatewayStatus.GREENGRASS_COMMUNICATION_ERROR]: 'status-warning',
  [GatewayStatus.GREENGRASS_DEPLOYING]: 'hourglass-half',
  [GatewayStatus.S3_BUCKET_ERROR]: 'status-failed',
};

export function GatewayStatusIcon(props: {gateway: InstrumentGateway}): JSX.Element {
  return (
    <>
      <Tooltip
        title={GATEWAY_STATUS_TO_TOOLTIP[props.gateway.status]}
        container="body"
        delay={{show: 500, hide: 100}}
      >
        <BenchlingIcon
          className={css(styles.statusIcon, styles[GATEWAY_STATUS_TO_STYLE[props.gateway.status]])}
          icon={GATEWAY_STATUS_TO_ICON[props.gateway.status]}
        />
      </Tooltip>
    </>
  );
}

function StatusCell(props: GatewayCellProps): JSX.Element {
  return (
    <Cell>
      <GatewayStatusIcon gateway={props.gateway} />
      {capitalize(GATEWAY_STATUS_TO_TEXT[props.gateway.status])}
    </Cell>
  );
}

interface ActionIconProps {
  label: string;
  icon: BenchlingIconName;
  disabled: boolean;
  onActivate: () => void;
  pointerClass: 'u-primary-pointer' | 'u-danger-pointer';
}

function ActionIcon(props: ActionIconProps): JSX.Element {
  const {label, icon, disabled, onActivate, pointerClass} = props;
  const handleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>): void => {
    if (disabled) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate();
    }
  };
  return (
    <Tooltip title={label} container="body">
      <BenchlingIcon
        aria-label={label}
        role="button"
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        className={classNames('faded', {
          [pointerClass]: !disabled,
          'u-cursor-default': disabled,
        })}
        icon={icon}
        onClick={disabled ? undefined : onActivate}
        onKeyDown={handleKeyDown}
      />
    </Tooltip>
  );
}

function ActionCell(props: GatewayCellProps & {refetch: () => void; isRowHovered: boolean}): JSX.Element {
  const disabled = props.gateway.isArchived;
  const visibility = props.isRowHovered ? 'visible' : 'hidden';
  return (
    <Cell>
      <span className={css(styles.actionButtons)} style={{visibility}}>
        <ActionIcon
          label="Edit gateway"
          icon="edit"
          disabled={disabled}
          pointerClass="u-primary-pointer"
          onActivate={() => {
            legacyRunModal(EditGatewayModal, {
              gateway: props.gateway,
              onSave: props.refetch,
            });
          }}
        />
        {props.gateway.gatewayType === GatewayType.S3 && (
          <ActionIcon
            label="View bucket info"
            icon="info-circle"
            disabled={disabled}
            pointerClass="u-primary-pointer"
            onActivate={async () => {
              const {roleArn} = await getS3GatewayInstaller();
              legacyRunModal(ViewBucketInfoModal, {
                roleArn,
                gatewayName: props.gateway.name,
                bucketName: props.gateway.fields?.[S3_FIELD_IDS.CUSTOMER_BUCKET] ?? '',
              });
            }}
          />
        )}
        <ActionIcon
          label="Archive gateway"
          icon="archive"
          disabled={disabled}
          pointerClass="u-danger-pointer"
          onActivate={() => {
            archiveGatewayWithMessage(props.gateway.id, props.refetch);
          }}
        />
      </span>
    </Cell>
  );
}

interface EditGatewayModalProps {
  gateway: InstrumentGateway;
  onRequestClose: () => void;
  onSave: () => void;
}

function stringFields(fields: InstrumentGateway['fields']): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [id, value] of Object.entries(fields ?? {})) {
    if (typeof value === 'string') {
      result[id] = value;
    }
  }
  return result;
}

const GENERIC_UPDATE_FAILURE_MESSAGE = 'Failed to update gateway. Please check your input and try again.';

const NAME_FIELD_ID = '__name__';

function EditGatewayModal(props: EditGatewayModalProps): JSX.Element {
  const [name, setName] = useState(props.gateway.name);
  const [error, setError] = useState<{fieldId: string | null; message: string} | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const {fields} = useGatewayFields(props.gateway.gatewayType);

  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.SECONDARY,
          submit: true,
          text: 'Save',
          disabled: fields == null,
        },
      ]}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={async () => {
        const trimmed = name.trim();
        if (!trimmed) {
          setError({fieldId: NAME_FIELD_ID, message: 'Name is required'});
          return;
        }
        const submitFields: Record<string, string> = {};
        for (const [id, value] of Object.entries(fieldValues)) {
          submitFields[id] = value.trim();
        }
        try {
          await withLoadingScreen(
            updateGateway(props.gateway.id, trimmed, submitFields),
            'Updating gateway...'
          );
          props.onSave();
          props.onRequestClose();
          // eslint-disable-next-line benchlint/always-throw-from-catch
        } catch (err: unknown) {
          setError({fieldId: null, message: getUserMessage(err) ?? GENERIC_UPDATE_FAILURE_MESSAGE});
        }
      }}
      title="Edit gateway"
      bannerProps={error ? {bannerType: InlineBannerType.ERROR, text: error.message} : null}
    >
      {fields == null ? (
        <CenteredHorizontalThrobber />
      ) : (
        <>
          <TextField
            label="Name"
            isRequired={true}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(null);
            }}
            hasError={error?.fieldId === NAME_FIELD_ID}
          />
          <br />
          <GatewayFieldsForm
            fields={fields}
            initialValues={stringFields(props.gateway.fields)}
            errorFieldId={error?.fieldId}
            onValuesChange={(values) => {
              setFieldValues(values);
              setError(null);
            }}
          />
        </>
      )}
    </Modal>
  );
}

interface InstrumentGatewaysPageProps {
  instrumentGateways: Array<InstrumentGateway> | null;
  refetch: () => void;
  hasMore: boolean;
  loadMore: () => void;
  isLoadingMore: boolean;
}

function InstrumentGatewaysPage(props: InstrumentGatewaysPageProps): JSX.Element {
  setWindowTitle(`Benchling Gateways`);
  const {instrumentGateways} = props;
  if (instrumentGateways == null) {
    return <CenteredHorizontalThrobber />;
  }
  return (
    <div className="settingsPage">
      <div
        className="settingsPage-contentWrapper"
        {...getTestProps({component: 'settingsPageContainer', element: 'root'})}
      >
        <div className="u-lr-split">
          <div style={{width: '70%'}}>
            <Header1>Benchling Gateways</Header1>
            <div className="help-block">
              <p>
                A Benchling gateway serves as a central hub for all your instrument connectors. Up to three
                gateways from multiple tenants can be installed on a single PC.
              </p>
              <ul>
                <li>
                  When creating a local gateway, the gateway installer only needs to be downloaded onto the
                  local machine once. However, an installer token must be generated for each tenant to install
                  gateways from multiple tenants.
                </li>
                <li>
                  When creating a S3 cloud gateway, the Benchling IAM Role must be added to your S3 bucket.
                </li>
              </ul>
            </div>
          </div>
          <div style={{float: 'right'}}>
            <div className="buttonGroup">
              <Button
                buttonStyle={ButtonStyle.PRIMARY}
                size={ButtonSize.SMALL}
                type="button"
                isDropdown={true}
                text="Setup Gateway"
              />
              <ul className="dropdown-menu pull-right">
                <li className={css(styles.dropdownSectionHeader)}>Cloud Gateway</li>
                <li>
                  <a
                    onClick={async () => {
                      const {roleArn} = await getS3GatewayInstaller();

                      legacyRunModal(CreateS3GatewayModal, {
                        roleArn,
                        onRequestClose: props.refetch,
                      });
                    }}
                  >
                    Configure S3 gateway
                  </a>
                </li>
                <li className={css(styles.dropdownSectionHeader)}>Local Gateway</li>
                <li>
                  <a
                    onClick={async () => {
                      const {downloadUrl} = await getGatewayInstaller('windows');
                      redirectForDownload(downloadUrl);
                      legacyRunModal(DownloadGatewayModal, {onRequestClose: props.refetch});
                    }}
                  >
                    Download gateway installer
                  </a>
                </li>
                {BenchlingConfig.INSTRUMENTS_ENABLE_LINUX_INSTALLER_FLOW && (
                  <li>
                    <a
                      onClick={async () => {
                        const {downloadUrl} = await getGatewayInstaller('linux');
                        redirectForDownload(downloadUrl);
                        legacyRunModal(DownloadGatewayModal, {onRequestClose: props.refetch});
                      }}
                    >
                      Download linux gateway installer
                    </a>
                  </li>
                )}

                <li>
                  <a
                    onClick={async () => {
                      const {downloadUrl} = await createInstallerTokenFile();
                      redirectForDownload(downloadUrl);
                      legacyRunModal(DownloadTokenModal, {onRequestClose: props.refetch});
                    }}
                  >
                    Generate new installer token
                  </a>
                </li>
                {window.debug && (
                  <li>
                    <a
                      onClick={() => {
                        legacyRunModal(CreateInstrumentGatewayModal, {
                          existingGateways: instrumentGateways,
                          onRequestSubmit: (name) => {
                            withLoadingScreen(
                              createGateway(name, 'GREENGRASS', undefined, true),
                              'Setting up instrument gateway...'
                            ).then((gatewayResponse) => {
                              if (gatewayResponse.tokenDownloadUrl) {
                                redirectForDownload(gatewayResponse.tokenDownloadUrl);
                              }
                              props.refetch();
                            });
                          },
                        });
                      }}
                    >
                      [DEV ONLY] Create Gateway for Local Dev
                    </a>
                  </li>
                )}
                <li>
                  <a
                    onClick={async () => {
                      const {downloadUrl} = await getCompatibilityScript();
                      redirectForDownload(downloadUrl);
                      legacyRunModal(DownloadScriptModal, {onRequestClose: props.refetch});
                    }}
                  >
                    Download PC compatibility script
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <Table numRows={instrumentGateways.length} overflowVisible={true} className={css(styles.table)}>
          <Column
            header="Name"
            renderCell={(rowIndex: number) => (
              <Cell>
                <span className={classNames({faded: instrumentGateways[rowIndex].isArchived})}>
                  {instrumentGateways[rowIndex].name}
                </span>
              </Cell>
            )}
          />
          <Column
            header="Type"
            renderCell={(rowIndex: number) => (
              <Cell>
                <span>{GATEWAY_TYPE_TO_DISPLAY[instrumentGateways[rowIndex].gatewayType]}</span>
              </Cell>
            )}
          />
          <Column
            header="Status"
            renderCell={(rowIndex: number) => <StatusCell gateway={instrumentGateways[rowIndex]} />}
          />
          <Column
            header="Actions"
            renderCell={(rowIndex: number, options) => (
              <ActionCell
                gateway={instrumentGateways[rowIndex]}
                refetch={props.refetch}
                isRowHovered={Boolean(options?.isRowHovered)}
              />
            )}
          />
        </Table>
        {props.hasMore && (
          <div className={css(styles.loadMore)}>
            {props.isLoadingMore ? (
              <CenteredHorizontalThrobber />
            ) : (
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                size={ButtonSize.SMALL}
                text="Load more"
                onClick={props.loadMore}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function InstrumentGatewaysPageDataFetcher(): JSX.Element {
  const [instrumentGateways, setInstrumentGateways] = useState(null as Array<InstrumentGateway> | null);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchPage = (token?: string, append = false): void => {
    const setLoading = append ? setIsLoadingMore : () => {};
    setLoading(true);
    listGatewaysPage(token)
      .then(({gateways, nextToken: newToken}) => {
        setInstrumentGateways((prev) =>
          naturalSortByKey(append && prev ? [...prev, ...gateways] : gateways, 'name')
        );
        setNextToken(newToken || undefined);
      })
      .catch((error) => {
        console.error('Failed to load instrument gateways page', error); // oxlint-disable-line no-console
        if (!append) {
          setInstrumentGateways([]);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const refetch = (): void => {
    setInstrumentGateways(null);
    setNextToken(undefined);
    fetchPage();
  };

  const loadMore = (): void => {
    if (nextToken) {
      fetchPage(nextToken, true);
    }
  };

  useOnMount(() => {
    fetchPage();
  });

  if (instrumentGateways == null) {
    return <CenteredHorizontalThrobber />;
  }
  return (
    <InstrumentGatewaysPage
      instrumentGateways={instrumentGateways}
      refetch={refetch}
      hasMore={Boolean(nextToken)}
      loadMore={loadMore}
      isLoadingMore={isLoadingMore}
    />
  );
}

export const styles = StyleSheet.create({
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  table: {
    marginTop: '35px',
  },
  loadMore: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '16px',
  },
  infoBanner: {
    marginBottom: '10px',
  },
  statusIcon: {
    marginRight: '6px',
  },
  versionWarningIcon: {
    color: Colors.ORANGE,
    marginLeft: '6px',
  },
  healthy: {
    color: Colors.GREEN,
  },
  unhealthy: {
    color: Colors.RED_LIGHT,
  },
  warning: {
    color: Colors.ORANGE,
  },
  unregistered: {
    color: Colors.GRAY_DARK,
  },
  actionButtons: {
    display: 'inline-flex',
    gap: '12px',
  },
  dropdownSectionHeader: {
    ...FONT_PROXIMA_10,
    color: Colors.GRAY_DARKER,
    fontStyle: 'italic',
    height: '23px',
    cursor: 'default',
    padding: '3px 10px',
  },
});
